import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Plus, Edit2, Trash2, CheckCircle, Clock, XCircle, MessageSquare, Mail, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AppHeader from '../components/AppHeader';
import Breadcrumb from '../components/Breadcrumb';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import TemplateButtonsEditor from '../components/TemplateButtonsEditor';
import TemplateHeaderFooter from '../components/TemplateHeaderFooter';
import {
  PageContainer, PageHeader, PageTitle, PageSubtitle,
  Card, CardHeader, CardTitle, Button, Badge, Grid, Flex,
  Label, FormGroup, Input, Select, EmptyState, EmptyIcon, EmptyText,
  ModalOverlay, ModalBox, ModalHeader, ModalTitle, ModalBody, ModalFooter,
  LoadingContainer, Spinner
} from '../components/ui';
import { theme } from '../styles/theme';
import api from '../services/api';

const STATUS_ICON    = { approved: CheckCircle, pending: Clock, rejected: XCircle, error: XCircle };
const STATUS_VARIANT = { approved: 'success', pending: 'warning', rejected: 'danger', error: 'danger' };
const CHANNEL_ICON   = { whatsapp: MessageSquare, email: Mail };

const EMPTY_FORM = {
  name: '', channel: 'whatsapp', type: 'marketing',
  businessAccountId: '', connectorId: '',
  waTemplateName: '', waTemplateLanguage: 'pt_BR',
  bodyText: '', variables: [],
  header: null, footer: null, buttons: [], limitedTimeOffer: null,
  subject: '', htmlBody: '', status: 'pending',
};

// Detecta {{1}}, {{2}}... no bodyText e sincroniza o array de variáveis
function syncVariables(bodyText, currentVars) {
  const matches = [...new Set((bodyText.match(/\{\{(\d+)\}\}/g) || []).map(m => parseInt(m.replace(/\D/g, ''))))].sort((a, b) => a - b);
  return matches.map(idx => {
    const existing = currentVars.find(v => v.index === idx);
    return existing || { index: idx, description: '', example: '' };
  });
}

// Renderiza bodyText substituindo {{N}} por valor de exemplo ou placeholder
function previewText(bodyText, variables) {
  return bodyText.replace(/\{\{(\d+)\}\}/g, (_, n) => {
    const v = variables.find(v => v.index === parseInt(n));
    return v?.example ? `[${v.example}]` : `[{{${n}}}]`;
  });
}

export default function MarketingTemplatesPage() {
  const { t } = useTranslation('mktpl');
  const { tenantId } = useParams();
  const toast   = useToast();
  const confirm = useConfirm();

  const [templates, setTemplates]         = useState([]);
  const [connectors, setConnectors]       = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showForm, setShowForm]           = useState(false);
  const [editing, setEditing]             = useState(null);
  const [form, setForm]                   = useState(EMPTY_FORM);
  const [saving, setSaving]               = useState(false);
  const [syncing, setSyncing]             = useState(null); // templateId em sync
  const [filterChannel, setFilterChannel] = useState('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const breadcrumbItems = [
    user.role === 'sallus_admin'
      ? { label: 'Libben Agents', path: '/sallus/dashboard' }
      : { label: t('home', { ns: 'common' }), path: `/dashboard/${tenantId}` },
    { label: t('title') }
  ];

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [tplRes, connRes] = await Promise.all([
        api.get(`/mktpl/${tenantId}`),
        api.get(`/connectors/${tenantId}`)
      ]);
      setTemplates(tplRes.data.templates || []);
      setConnectors(connRes.data.connectors || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  // Agrupa conectores WhatsApp por businessAccountId
  const whatsappBusinessAccounts = (() => {
    const map = {};
    connectors.filter(c => c.enabled && c.type === 'whatsapp').forEach(c => {
      const bid = c.businessAccountId || c.connectorId; // fallback: usa connectorId se sem WABA
      if (!map[bid]) map[bid] = { businessAccountId: bid, label: c.businessAccountId ? `WABA ${bid}` : c.name, connectors: [] };
      map[bid].connectors.push(c);
    });
    return Object.values(map);
  })();
  const emailConnectors = connectors.filter(c => c.enabled && c.type === 'email');

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleBodyTextChange = (text) => {
    setForm(p => ({
      ...p,
      bodyText: text,
      variables: syncVariables(text, p.variables)
    }));
  };

  const handleHtmlBodyChange = (text) => {
    setForm(p => ({
      ...p,
      htmlBody: text,
      variables: syncVariables(text, p.variables)
    }));
  };

  const setVarField = (idx, field, value) => {
    setForm(p => {
      const vars = p.variables.map(v => v.index === idx ? { ...v, [field]: value } : v);
      return { ...p, variables: vars };
    });
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit = (tpl) => {
    setEditing(tpl);
    setForm({
      name: tpl.name, channel: tpl.channel, type: tpl.type,
      businessAccountId: tpl.businessAccountId || '',
      connectorId: tpl.connectorId || '',
      waTemplateName: tpl.waTemplateName || '',
      waTemplateLanguage: tpl.waTemplateLanguage || 'pt_BR',
      bodyText: tpl.bodyText || '',
      variables: tpl.variables || [],
      header: tpl.header || null,
      footer: tpl.footer || null,
      buttons: tpl.buttons || [],
      limitedTimeOffer: tpl.limitedTimeOffer || null,
      subject: tpl.subject || '',
      htmlBody: tpl.htmlBody || '',
      status: tpl.status || 'pending',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.channel || !form.type) { toast.error(t('fillRequired')); return; }
    if (form.channel === 'whatsapp' && !form.businessAccountId) { toast.error(t('businessAccountRequired')); return; }
    if (form.channel === 'whatsapp' && !form.waTemplateName) { toast.error(t('waNameRequired')); return; }
    if (form.channel === 'email' && !form.connectorId) { toast.error(t('connectorRequired')); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/mktpl/${tenantId}/${editing.templateId}`, form);
        toast.success(t('updated'));
      } else {
        const res = await api.post(`/mktpl/${tenantId}`, form);
        if (res.data.metaError) {
          toast.error(`${t('metaError')}: ${res.data.metaError}`);
        } else {
          toast.success(t('created'));
        }
      }
      setShowForm(false);
      load();
    } catch (e) { toast.error(e.response?.data?.message || e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (tpl) => {
    const ok = await confirm({ title: t('deleteConfirm', { name: tpl.name }), danger: true });
    if (!ok) return;
    try {
      await api.delete(`/mktpl/${tenantId}/${tpl.templateId}`);
      toast.success(t('deleted'));
      load();
    } catch (e) { toast.error(e.response?.data?.message || e.message); }
  };

  const handleSync = async (tpl) => {
    setSyncing(tpl.templateId);
    try {
      const res = await api.post(`/mktpl/${tenantId}/${tpl.templateId}/sync`);
      toast.success(t('syncSuccess', { status: t(`status.${res.data.status}`, { defaultValue: res.data.status }) }));
      load();
    } catch (e) { toast.error(e.response?.data?.message || e.message); }
    finally { setSyncing(null); }
  };

  const filtered = filterChannel ? templates.filter(t => t.channel === filterChannel) : templates;

  if (loading) return (
    <>
      <AppHeader title={t('title')} />
      <PageContainer><LoadingContainer><Spinner /><span>{t('loading', { ns: 'common' })}</span></LoadingContainer></PageContainer>
    </>
  );

  return (
    <>
      <AppHeader title={t('title')} />
      <PageContainer>
        <Breadcrumb items={breadcrumbItems} />
        <PageHeader>
          <div>
            <PageTitle>📋 {t('title')}</PageTitle>
            <PageSubtitle>{t('subtitle')}</PageSubtitle>
          </div>
          <Button variant="primary" size="sm" onClick={openCreate}><Plus size={14} /> {t('new')}</Button>
        </PageHeader>

        {templates.length > 0 && (
          <FilterRow>
            <FilterBtn $active={!filterChannel} onClick={() => setFilterChannel('')}>{t('all')} ({templates.length})</FilterBtn>
            <FilterBtn $active={filterChannel === 'whatsapp'} onClick={() => setFilterChannel(filterChannel === 'whatsapp' ? '' : 'whatsapp')}>📱 WhatsApp ({templates.filter(t => t.channel === 'whatsapp').length})</FilterBtn>
            <FilterBtn $active={filterChannel === 'email'} onClick={() => setFilterChannel(filterChannel === 'email' ? '' : 'email')}>📧 Email ({templates.filter(t => t.channel === 'email').length})</FilterBtn>
          </FilterRow>
        )}

        {filtered.length === 0 ? (
          <EmptyState>
            <EmptyIcon><MessageSquare size={48} strokeWidth={1} /></EmptyIcon>
            <EmptyText>{t('empty')}</EmptyText>
            <Button variant="primary" size="sm" onClick={openCreate} style={{ marginTop: theme.spacing[3] }}>
              <Plus size={14} /> {t('newFirst')}
            </Button>
          </EmptyState>
        ) : (
          <Grid min="300px">
            {filtered.map(tpl => {
              const StatusIcon = STATUS_ICON[tpl.status] || Clock;
              const ChanIcon   = CHANNEL_ICON[tpl.channel] || MessageSquare;
              return (
                <Card key={tpl.templateId}>
                  <CardHeader>
                    <Flex gap={theme.spacing[2]} style={{ alignItems: 'center' }}>
                      <ChanIcon size={14} color={theme.colors.primary} />
                      <CardTitle style={{ flex: 1 }}>{tpl.name}</CardTitle>
                      <Badge variant={STATUS_VARIANT[tpl.status] || 'neutral'}>
                        <StatusIcon size={10} style={{ marginRight: 4 }} />
                        {t(`status.${tpl.status}`, { defaultValue: tpl.status })}
                      </Badge>
                    </Flex>
                  </CardHeader>
                  <MetaList>
                    <MetaItem><strong>{t('type')}:</strong> {t(`types.${tpl.type}`, { defaultValue: tpl.type })}</MetaItem>
                    <MetaItem><strong>ID:</strong> <code style={{ fontSize: '10px', background: '#f3f4f6', padding: '1px 4px', borderRadius: '3px' }}>{tpl.templateId}</code></MetaItem>
                    {tpl.waTemplateName && <MetaItem><strong>{t('waName')}:</strong> {tpl.waTemplateName}</MetaItem>}
                    {tpl.businessAccountId && <MetaItem><strong>WABA:</strong> {tpl.businessAccountId}</MetaItem>}
                    {!tpl.businessAccountId && tpl.connectorId && (() => { const conn = connectors.find(c => c.connectorId === tpl.connectorId); return conn ? <MetaItem><strong>{t('connector')}:</strong> {conn.name}</MetaItem> : null; })()}
                    {tpl.metaError && <MetaError>⚠ {tpl.metaError}</MetaError>}
                    {tpl.bodyText && <BodyPreview>{previewText(tpl.bodyText, tpl.variables || [])}</BodyPreview>}
                    {tpl.variables?.length > 0 && (
                      <MetaItem><strong>{t('variables')}:</strong> {tpl.variables.map(v => `{{${v.index}}} ${v.description}`).join(' · ')}</MetaItem>
                    )}
                  </MetaList>
                  <Flex gap={theme.spacing[2]} style={{ marginTop: theme.spacing[3] }}>
                    <Button variant="secondary" size="sm" style={{ flex: 1 }} onClick={() => openEdit(tpl)}><Edit2 size={12} /> {t('edit', { ns: 'common' })}</Button>
                    {tpl.channel === 'whatsapp' && (
                      <Button variant="secondary" size="sm" onClick={() => handleSync(tpl)} disabled={syncing === tpl.templateId} title={t('syncHint')}>
                        <RefreshCw size={12} className={syncing === tpl.templateId ? 'spin' : ''} />
                      </Button>
                    )}
                    <Button variant="danger" size="sm" onClick={() => handleDelete(tpl)}><Trash2 size={12} /></Button>
                  </Flex>
                </Card>
              );
            })}
          </Grid>
        )}

        {showForm && (
          <ModalOverlay onClick={() => setShowForm(false)}>
            <ModalBox maxWidth="600px" onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{editing ? t('edit') : t('new')}</ModalTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>✕</Button>
              </ModalHeader>
              <ModalBody>

                <Flex gap={theme.spacing[3]}>
                  <FormGroup style={{ flex: 1 }}>
                    <Label>{t('nameField')} *</Label>
                    <Input value={form.name} onChange={e => setField('name', e.target.value)} placeholder={t('namePlaceholder')} />
                  </FormGroup>
                </Flex>

                <Flex gap={theme.spacing[3]}>
                  <FormGroup style={{ flex: 1 }}>
                    <Label>{t('channel')} *</Label>
                    <Select value={form.channel} onChange={e => { setField('channel', e.target.value); setField('connectorId', ''); }}>
                      <option value="whatsapp">📱 WhatsApp</option>
                      <option value="email">📧 Email</option>
                    </Select>
                  </FormGroup>
                  <FormGroup style={{ flex: 1 }}>
                    <Label>{t('type')} *</Label>
                    <Select value={form.type} onChange={e => setField('type', e.target.value)}>
                      <option value="marketing">{t('types.marketing')}</option>
                      <option value="utility">{t('types.utility')}</option>
                    </Select>
                  </FormGroup>
                </Flex>

                <FormGroup>
                  <Label>{t('connectorField')} *</Label>
                  <Select value={form.channel === 'whatsapp' ? form.businessAccountId : form.connectorId}
                    onChange={e => form.channel === 'whatsapp'
                      ? setField('businessAccountId', e.target.value)
                      : setField('connectorId', e.target.value)
                    }>
                    <option value="">{t('selectConnector')}</option>
                    {form.channel === 'whatsapp'
                      ? whatsappBusinessAccounts.map(ba => (
                          <option key={ba.businessAccountId} value={ba.businessAccountId}>
                            {ba.label} ({ba.connectors.map(c => c.name).join(', ')})
                          </option>
                        ))
                      : emailConnectors.map(c => (
                          <option key={c.connectorId} value={c.connectorId}>{c.name}</option>
                        ))
                    }
                  </Select>
                  {form.channel === 'whatsapp' && <Helper>{t('businessAccountHint')}</Helper>}
                </FormGroup>

                {form.channel === 'whatsapp' && (
                  <>
                    <Flex gap={theme.spacing[3]}>
                      <FormGroup style={{ flex: 2 }}>
                        <Label>{t('waName')} *</Label>
                        <Input value={form.waTemplateName} onChange={e => setField('waTemplateName', e.target.value)} placeholder="ex: boas_vindas_v1" />
                      </FormGroup>
                      <FormGroup style={{ flex: 1 }}>
                        <Label>{t('waLanguage')}</Label>
                        <Select value={form.waTemplateLanguage} onChange={e => setField('waTemplateLanguage', e.target.value)}>
                          <option value="pt_BR">pt_BR</option>
                          <option value="en_US">en_US</option>
                          <option value="es">es</option>
                        </Select>
                      </FormGroup>
                    </Flex>

                    <FormGroup>
                      <Label>{t('bodyText')} *</Label>
                      <Textarea
                        rows={4}
                        value={form.bodyText}
                        onChange={e => handleBodyTextChange(e.target.value)}
                        placeholder={t('bodyTextPlaceholder')}
                      />
                      <Helper>{t('bodyTextHint')}</Helper>
                    </FormGroup>

                    {form.variables.length > 0 && (
                      <>
                        <SectionLabel>{t('variablesSection')}</SectionLabel>
                        {form.bodyText && (
                          <BodyPreviewBox>{previewText(form.bodyText, form.variables)}</BodyPreviewBox>
                        )}
                        {form.variables.map(v => (
                          <VarRow key={v.index}>
                            <VarIndex>{'{{' + v.index + '}}'}</VarIndex>
                            <Input
                              value={v.description}
                              onChange={e => setVarField(v.index, 'description', e.target.value)}
                              placeholder={t('varDesc')}
                              style={{ flex: 2 }}
                            />
                            <Input
                              value={v.example}
                              onChange={e => setVarField(v.index, 'example', e.target.value)}
                              placeholder={t('varExample')}
                              style={{ flex: 1 }}
                            />
                          </VarRow>
                        ))}
                      </>
                    )}

                    <TemplateHeaderFooter
                      header={form.header}
                      footer={form.footer}
                      limitedTimeOffer={form.limitedTimeOffer}
                      onChange={({ header, footer, limitedTimeOffer }) =>
                        setForm(p => ({ ...p, header, footer, limitedTimeOffer }))
                      }
                    />

                    <TemplateButtonsEditor
                      buttons={form.buttons}
                      onChange={buttons => setField('buttons', buttons)}
                    />

                    <FormGroup style={{ marginTop: theme.spacing[3] }}>
                      <Label>{t('statusField')}</Label>
                      <StatusReadonly $status={form.status}>
                        {form.status === 'approved' && '✅ '}
                        {form.status === 'pending' && '⏳ '}
                        {form.status === 'rejected' && '❌ '}
                        {t(`status.${form.status}`)}
                      </StatusReadonly>
                      <Helper>{t('statusHint')}</Helper>
                    </FormGroup>
                  </>
                )}

                {form.channel === 'email' && (
                  <>
                    <FormGroup>
                      <Label>{t('subject')}</Label>
                      <Input value={form.subject} onChange={e => setField('subject', e.target.value)} placeholder={t('subjectPlaceholder')} />
                    </FormGroup>
                    <FormGroup>
                      <Label>{t('htmlBody')}</Label>
                      <Textarea rows={6} value={form.htmlBody} onChange={e => handleHtmlBodyChange(e.target.value)} placeholder={t('htmlBodyPlaceholder')} />
                      <Helper>{t('bodyTextHint')}</Helper>
                    </FormGroup>
                    {form.variables.length > 0 && (
                      <>
                        <SectionLabel>{t('variablesSection')}</SectionLabel>
                        {form.variables.map(v => (
                          <VarRow key={v.index}>
                            <VarIndex>{'{{' + v.index + '}}'}</VarIndex>
                            <Input
                              value={v.description}
                              onChange={e => setVarField(v.index, 'description', e.target.value)}
                              placeholder={t('varDesc')}
                              style={{ flex: 2 }}
                            />
                            <Input
                              value={v.example}
                              onChange={e => setVarField(v.index, 'example', e.target.value)}
                              placeholder={t('varExample')}
                              style={{ flex: 1 }}
                            />
                          </VarRow>
                        ))}
                      </>
                    )}
                  </>
                )}

              </ModalBody>
              <ModalFooter>
                <Button variant="secondary" onClick={() => setShowForm(false)}>{t('cancel', { ns: 'common' })}</Button>
                <Button variant="primary" onClick={handleSave} disabled={saving}>
                  {saving ? t('saving', { ns: 'common' }) : editing ? t('update', { ns: 'common' }) : t('create', { ns: 'common' })}
                </Button>
              </ModalFooter>
            </ModalBox>
          </ModalOverlay>
        )}
      </PageContainer>
    </>
  );
}

const FilterRow      = styled.div`display: flex; gap: ${theme.spacing[2]}; flex-wrap: wrap; margin-bottom: ${theme.spacing[4]};`;
const FilterBtn      = styled.button`padding: ${theme.spacing[1]} ${theme.spacing[3]}; border-radius: ${theme.radii.full}; font-size: ${theme.typography.xs}; font-weight: ${theme.typography.medium}; cursor: pointer; border: 1px solid ${theme.colors.border}; background: ${p => p.$active ? theme.colors.primary : theme.colors.white}; color: ${p => p.$active ? 'white' : theme.colors.textSecondary}; transition: ${theme.transitions.fast};`;
const MetaList       = styled.div`display: flex; flex-direction: column; gap: 2px; margin: ${theme.spacing[2]} 0;`;
const MetaItem       = styled.span`font-size: ${theme.typography.xs}; color: ${theme.colors.textSecondary};`;
const BodyPreview    = styled.p`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; font-style: italic; margin: ${theme.spacing[1]} 0; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;`;
const BodyPreviewBox = styled.div`background: ${theme.colors.gray50}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md}; padding: ${theme.spacing[3]}; font-size: ${theme.typography.sm}; color: ${theme.colors.textSecondary}; line-height: 1.5; margin-bottom: ${theme.spacing[3]}; white-space: pre-wrap;`;
const SectionLabel   = styled.div`font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textSecondary}; text-transform: uppercase; letter-spacing: 0.05em; margin: ${theme.spacing[3]} 0 ${theme.spacing[2]}; padding-top: ${theme.spacing[3]}; border-top: 1px solid ${theme.colors.border};`;
const Helper         = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; margin-top: ${theme.spacing[1]};`;
const Textarea       = styled.textarea`width: 100%; padding: ${theme.spacing[2]} ${theme.spacing[3]}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md}; font-size: ${theme.typography.sm}; font-family: inherit; resize: vertical; box-sizing: border-box; &:focus { outline: none; border-color: ${theme.colors.primary}; }`;
const VarRow         = styled.div`display: flex; gap: ${theme.spacing[2]}; align-items: center; margin-bottom: ${theme.spacing[2]};`;
const VarIndex       = styled.span`font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold}; font-family: monospace; background: ${theme.colors.primaryLight}; color: ${theme.colors.primary}; padding: 2px ${theme.spacing[2]}; border-radius: ${theme.radii.sm}; min-width: 40px; text-align: center;`;
const MetaError      = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.danger}; margin: ${theme.spacing[1]} 0; line-height: 1.4;`;
const StatusReadonly  = styled.div`padding: ${theme.spacing[2]} ${theme.spacing[3]}; border-radius: ${theme.radii.md}; font-size: ${theme.typography.sm}; font-weight: ${theme.typography.medium}; background: ${p => p.$status === 'approved' ? '#ecfdf5' : p.$status === 'rejected' ? '#fef2f2' : '#fffbeb'}; color: ${p => p.$status === 'approved' ? '#065f46' : p.$status === 'rejected' ? '#991b1b' : '#92400e'}; border: 1px solid ${p => p.$status === 'approved' ? '#a7f3d0' : p.$status === 'rejected' ? '#fecaca' : '#fde68a'};`;
